import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import Text from '@/components/switchback/Text/Text';
import { generateSimpleId } from '@/utility/helpers';
import { mergeRefs } from '@/utility/mergeRefs';
import { convertPixelsToRem, getElComputedStyle } from '@/utility/styles';

import css from './TextArea.module.css';

type ITextAreaElement = React.TextareaHTMLAttributes<HTMLTextAreaElement>;

interface ITextAreaProps extends ITextAreaElement {
  'data-testid'?: string;
  /**
   * Initial value of the input.
   * Best used if you plan to use this component under uncontrolled mode.
   */
  defaultValue?: string;
  /**
   * Validation error to be displayed
   */
  error?: string;
  label?: string;
  /**
   * TextArea max-height
   */
  maxRow?: number;
  /**
   * * TextArea min-height
   */
  minRow?: number;
  value?: string;
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  /**
   * setValue: Utility method from `react-use-form`
   * see: https://github.com/react-hook-form/react-hook-form/discussions/1710
   *
   * @param name
   * @param value
   * @param config
   */
  setValue?: (name: string, value: string, config: { shouldValidate: boolean }) => void;
}

const TextArea = React.forwardRef<HTMLTextAreaElement, ITextAreaProps>(
  (
    {
      className,
      ['data-testid']: testid,
      defaultValue = '',
      id,
      name,
      label,
      minRow = 3,
      maxRow = 5,
      error,
      required,
      rows,
      style,
      value,
      onFocus,
      onBlur,
      onChange,
      setValue,
      ...props
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = useState(defaultValue);
    const [hasFocus, setHasFocus] = useState(false);
    const hasValue = !!internalValue || !!value;
    const isControlled = typeof value !== 'undefined';
    const inputId = useMemo(() => id || `textarea_${generateSimpleId()}`, [id]);
    const shouldResize = useMemo(() => !rows && minRow !== maxRow, [rows, minRow, maxRow]);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    // lineHeight is necessary in order to set min-height and
    // max-height according to the number of rows provided
    const lineHeightSize = useMemo(() => {
      if (!textareaRef.current) return 1;
      const lh = getElComputedStyle(textareaRef.current, 'line-height') || '24';
      return convertPixelsToRem(parseInt(lh, 10));
    }, [textareaRef.current]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-grow the native textarea between `minRow` and `maxRow` lines. Height is
    // measured off `scrollHeight` and clamped in pixels (using the live element's
    // own computed line-height, so this is correct on the very first measurement
    // rather than depending on a re-render to pick up `lineHeightSize`); once
    // content exceeds `maxRow` lines the textarea scrolls its own overflow
    // (standard browser behaviour) instead of growing further.
    const resizeTextarea = useCallback(() => {
      const el = textareaRef.current;
      if (!shouldResize || !el) return;

      const lineHeightPx = parseInt(getElComputedStyle(el, 'line-height') || '24', 10);
      const minHeightPx = minRow * lineHeightPx;
      const maxHeightPx = maxRow * lineHeightPx;

      el.style.height = 'auto';
      el.style.height = `${Math.min(Math.max(el.scrollHeight, minHeightPx), maxHeightPx)}px`;
    }, [shouldResize, minRow, maxRow]);

    // Resize on mount and whenever the (controlled or uncontrolled) value,
    // row bounds, or line-height measurement change.
    const currentValue = isControlled ? value : internalValue;
    useLayoutEffect(() => {
      resizeTextarea();
    }, [resizeTextarea, currentValue]);

    const mergedStyle =
      style || shouldResize
        ? {
            ...style,
            ...(shouldResize && {
              minHeight: `${minRow * lineHeightSize}rem`,
              maxHeight: `${maxRow * lineHeightSize}rem`,
            }),
          }
        : undefined;

    return (
      <div className={`${className} relative`}>
        <div
          className={css.container}
          data-testid="textarea-wrapper"
          data-error={String(!!error)}
          data-focused={String(hasFocus)}
          data-empty={String(!hasValue)}>
          {label && (
            <label className={css.label} htmlFor={inputId}>
              {label}
            </label>
          )}
          <textarea
            aria-invalid={!!error}
            className={`w-full h-full outline-none resize-none ${
              shouldResize ? 'overflow-y-auto' : ''
            }`}
            data-testid={testid || `${name}-textarea`}
            id={inputId}
            name={name}
            ref={mergeRefs(ref, textareaRef)}
            required={required}
            rows={shouldResize ? undefined : rows || minRow || maxRow}
            style={mergedStyle}
            value={isControlled ? value : internalValue}
            {...props}
            onFocus={e => {
              setHasFocus(true);
              onFocus?.(e);
            }}
            onBlur={e => {
              setHasFocus(false);
              onBlur?.(e);
            }}
            onChange={e => {
              onChange?.(e);
              setInternalValue(e.currentTarget.value);
              name && setValue && setValue(name, e.currentTarget.value, { shouldValidate: true });
              resizeTextarea();
            }}
          />
        </div>
        {error && (
          <Text
            data-testid={`${name}-textarea-error`}
            type="inline"
            className={`${css.error} absolute top-full left-0 mt-1`}>
            {error}
          </Text>
        )}
      </div>
    );
  },
);

TextArea.displayName = 'TextArea';

export default TextArea;
